import {
  Box,
  Button,
  FlexBox,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Tooltip,
  WindowProvider,
} from '@dixa/lib-shared-habla';
import { HookFormText } from 'lib-shared-hook-form';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { AttachmentsFieldList } from 'assets/components/shared/util/richTextField/components/components/attachmentsFieldList/attachmentsFieldList';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import {
  HookFormCategorizedTagsSelect,
  HookFormEmailSelect,
  RichTextFieldRef,
} from 'components/hookForm';
// eslint-disable-next-line no-restricted-imports
import { ToggleBox, ToggleBoxBody, ToggleBoxHead } from 'fable/toggleBox';
import { useCommandHandler } from 'utils/commands';
import { createAppModal } from 'utils/modal';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useServerStorage } from 'utils/useServerStorage/useServerStorage';

import { ComposeCustomAttributesToggle } from '../composeCustomAttributes';

import { ComposeEmailFieldCsatLanguage } from './components/composeEmailFieldCsatLanguage';
import { ComposeEmailFieldEmailContact } from './components/composeEmailFieldEmailContact';
import { ComposeEmailFieldRichText } from './components/composeEmailFieldRichText';
import { ComposeEmailLinkedBanner } from './components/composeEmailLinkedBanner';
import { ComposeEmailQueueSection } from './components/composeEmailQueueSection';
import ComposeWindowPortal from './components/composeWindowPortal';
import { SubjectTranslateButton } from './components/subjectTranslateButton';
import styles from './composeEmailModal.module.scss';
import type { FormValues } from './composeEmailModal.types';
import { convertEmailDraftV2ToFormValues } from './composeEmailModal.utils';
import { useAttachments } from './hooks/useAttachments';
import { useCcBcc } from './hooks/useCcBcc';
import { useComposeEmailSubmit } from './hooks/useComposeEmailSubmit';
import { useEmailDraft } from './hooks/useEmailDraft';
import { useLinkedConversationData } from './hooks/useLinkedConversationData';

const formId = 'compose-email';

export const ComposeEmailModal = createAppModal(
  { type: 'app:composeEmail' },
  (props): JSX.Element => {
    const {
      isOpen,
      hide,
      modal: { arg: { draft, initialValues } = {} } = {},
    } = props;

    const bodyEditorRef = useRef<RichTextFieldRef>(null);

    const parentConversationInitial =
      initialValues?.parentConversation ?? draft?.parentConversation ?? null;
    const linkTypeInitial = initialValues?.linkType ?? draft?.linkType ?? null;
    const { getData: getLinkedData, isLoading: linkedDataLoading } =
      useLinkedConversationData(parentConversationInitial);
    const [isFillingTranscript, setIsFillingTranscript] = useState(
      !draft && linkTypeInitial === 'email_forward' && !initialValues?.body,
    );

    const { data: userStorage } = useServerStorage('user');
    const lastUsedEmail = userStorage?.global?.lastUsedEmailIntegration;
    const { data: orgStorage } = useServerStorage('organization');
    const autofillFromEnabled = orgStorage?.autofill_email_from !== false;

    const form = useForm<FormValues>({
      defaultValues: {
        toEmail: [],
        fromEmail:
          autofillFromEnabled && lastUsedEmail ? [lastUsedEmail] : null,
        cc: [],
        bcc: [],
        subject: '',
        attachments_v2: [],
        body: '',
        offerInQueue: false,
        queueId: null,
        tags: [],
        offer: false,
        includeInternalNote: false,
        internalNote: '',
        linkType: null,
        parentConversation: null,
        customAttributes: {},
        customAttributesOpen: false,
        satisfactionLanguage: null,
        cancelledSatisfaction: false,
        ...(draft ? convertEmailDraftV2ToFormValues(draft) : {}),
        ...(initialValues ?? {}),
      },
      mode: 'all',
    });
    const {
      control,
      formState: { isValid, isSubmitting },
      getValues,
      handleSubmit,
      watch,
      setValue,
    } = form;

    const parentConversation = watch('parentConversation');
    const linkType = watch('linkType');
    const fromEmail = watch('fromEmail');
    const toEmail = watch('toEmail');
    const rawEnduserId = toEmail?.[0]?.id ?? null;
    const recipientEnduserId =
      rawEnduserId === 'suggestion' ? null : rawEnduserId;
    const fromEmailValue = fromEmail?.[0] ?? null;
    const queue = watch('queueId');
    const showInternalNote = watch('includeInternalNote');
    const offerInQueue = watch('offerInQueue');
    const [subjectWarning, setSubjectWarning] = useState<string>();
    const setSubject = useCallback(
      (value: string) => {
        setSubjectWarning(undefined);
        setValue('subject', value, { shouldDirty: true, shouldValidate: true });
      },
      [setValue],
    );
    const subjectField = useMemo(
      () => ({ get: () => getValues('subject'), set: setSubject }),
      [getValues, setSubject],
    );
    const { hasCopilot } = useCopilotTrial();
    const handleSubjectTranslateError = useCallback(
      (message: string) => {
        setSubjectWarning(message);
        // The field only shows its alert once touched.
        setValue('subject', getValues('subject'), { shouldTouch: true });
      },
      [getValues, setValue],
    );

    const { ccEnabled, bccEnabled, ccOnClick, bccOnClick } = useCcBcc({
      initialCcEnabled: watch('cc').length > 0,
      initialBccEnabled: watch('bcc').length > 0,
      setValue,
    });

    const { handleSaveDraft, deleteDraft } = useEmailDraft({
      draft,
      getValues,
      watch,
      hide,
    });

    useCommandHandler({
      name: 'conversation:saveEmailDraft',
      actionFn: handleSaveDraft,
    });

    const { onSubmit, sendFailed } = useComposeEmailSubmit({
      deleteDraft,
      hide,
    });

    const { attachmentFieldsAdapter, handleAttachmentAdded } = useAttachments({
      control,
    });

    const handleChangeSubject = useCallback(
      (subject: string) => {
        if (!getValues('subject')) {
          setValue('subject', subject);
        }
      },
      [setValue, getValues],
    );

    useEffect(() => {
      if (!queue?.length && offerInQueue) {
        setValue('offerInQueue', false);
      }
    }, [queue, offerInQueue, setValue]);

    const insertTranscript = useCallback(
      (
        body: string,
        subject: string,
        attachments: FormValues['attachments_v2'],
      ) => {
        const currentBody = getValues('body');
        const newBody = currentBody ? `${currentBody}\n\n${body}` : body;
        setValue('body', newBody);
        bodyEditorRef.current?.setText(newBody);

        if (!getValues('subject')) {
          setValue('subject', subject);
        }

        const existing = getValues('attachments_v2');
        const newAttachments = attachments.filter(
          (a) => !existing.some((e) => e.url === a.url),
        );
        setValue('attachments_v2', [...existing, ...newAttachments]);
      },
      [getValues, setValue],
    );

    const [isInsertingTranscript, setIsInsertingTranscript] = useState(false);
    const isBuildingTranscript = isFillingTranscript || isInsertingTranscript;

    const handleCopyPaste = useCallback(async () => {
      setIsInsertingTranscript(true);
      const linkedData = await getLinkedData();
      setIsInsertingTranscript(false);
      if (!linkedData) return;

      insertTranscript(
        linkedData.body,
        linkedData.subject,
        linkedData.attachments,
      );
    }, [getLinkedData, insertTranscript]);

    useEffect(() => {
      if (!isFillingTranscript || linkedDataLoading) return;

      let cancelled = false;
      getLinkedData().then((linkedData) => {
        if (cancelled) return;

        if (linkedData) {
          insertTranscript(
            linkedData.forwardBody,
            linkedData.forwardSubject,
            linkedData.attachments,
          );
        }
        setIsFillingTranscript(false);
      });

      return () => {
        cancelled = true;
      };
    }, [
      isFillingTranscript,
      linkedDataLoading,
      getLinkedData,
      insertTranscript,
    ]);

    const [showWindowPortal, setShowWindowPortal] = useState(false);
    const [popoutWindow, setPopoutWindow] = useState<Window | undefined>(
      undefined,
    );

    const toggleWindowPortal = useCallback(
      () => setShowWindowPortal((v) => !v),
      [],
    );

    const closeWindowPortal = useCallback(() => {
      setShowWindowPortal(false);
      setPopoutWindow(undefined);
    }, []);

    useEffect(() => {
      if (!isOpen && showWindowPortal) {
        setShowWindowPortal(false);
        setPopoutWindow(undefined);
      }
    }, [isOpen, showWindowPortal]);

    const [isBodyUploading, setIsBodyUploading] = useState(false);
    const [isNoteUploading, setIsNoteUploading] = useState(false);
    const isAnyUploading = isBodyUploading || isNoteUploading;

    useEffect(() => {
      if (!showInternalNote) {
        setIsNoteUploading(false);
      }
    }, [showInternalNote]);

    const handleTriggerSend = useCallback(() => {
      if (isAnyUploading || isBuildingTranscript) return;
      handleSubmit(onSubmit)();
    }, [handleSubmit, onSubmit, isAnyUploading, isBuildingTranscript]);

    const modalContent = (
      <>
        <ModalHead
          title="Compose new email"
          onClose={hide}
          secondaryButton={{
            onClick: toggleWindowPortal,
            tooltipContent: showWindowPortal
              ? 'Show in main window'
              : 'Open in new window',
            icon: showWindowPortal ? 'compress' : 'external-link',
          }}
        />

        <ModalBody>
          <form
            onSubmit={
              isAnyUploading || isBuildingTranscript
                ? (e) => e.preventDefault()
                : handleSubmit(onSubmit)
            }
            id={formId}
          >
            <FormGrid>
              {parentConversation && (
                <FormField span={6}>
                  <ComposeEmailLinkedBanner
                    linkType={linkType}
                    parentConversation={parentConversation}
                    linkedDataLoading={
                      linkedDataLoading || isBuildingTranscript
                    }
                    onCopyPaste={handleCopyPaste}
                  />
                </FormField>
              )}

              <FormField span={6}>
                <ComposeEmailFieldEmailContact
                  name="toEmail"
                  label="To"
                  required
                  componentProps={{
                    suffix: {
                      type: 'custom',
                      noWrap: true,
                      value: (
                        <>
                          <Button
                            variant={ccEnabled ? 'primary' : 'tertiary'}
                            className={styles.suffixButton}
                            onClick={ccOnClick}
                          >
                            CC
                          </Button>
                          <Button
                            variant={bccEnabled ? 'primary' : 'tertiary'}
                            className={styles.suffixButton}
                            onClick={bccOnClick}
                          >
                            BCC
                          </Button>
                        </>
                      ),
                    },
                  }}
                />
              </FormField>

              {(ccEnabled || bccEnabled) && (
                <FormField>
                  <FlexBox padding="250 400" className={styles.optionalBox}>
                    <FormGrid>
                      {ccEnabled && (
                        <FormField span={6}>
                          <ComposeEmailFieldEmailContact
                            name="cc"
                            label="CC"
                            multiple
                          />
                        </FormField>
                      )}
                      {bccEnabled && (
                        <FormField span={6}>
                          <ComposeEmailFieldEmailContact
                            name="bcc"
                            label="BCC"
                            multiple
                          />
                        </FormField>
                      )}
                    </FormGrid>
                  </FlexBox>
                </FormField>
              )}

              <FormField>
                <HookFormEmailSelect
                  control={control}
                  name="fromEmail"
                  label="From"
                />
              </FormField>

              <FormField>
                <HookFormText
                  name="subject"
                  control={control}
                  label="Subject"
                  rules={{ required: 'Subject is required' }}
                  meta={{ warning: subjectWarning }}
                  componentProps={{
                    onChange: () => setSubjectWarning(undefined),
                    suffix: hasCopilot
                      ? {
                          type: 'custom',
                          noWrap: true,
                          value: (
                            <SubjectTranslateButton
                              onChange={setSubject}
                              onError={handleSubjectTranslateError}
                            />
                          ),
                        }
                      : undefined,
                  }}
                />
              </FormField>

              <FormField>
                <ComposeEmailFieldRichText
                  name="body"
                  label="Message"
                  placeholder="Type a message"
                  required
                  editorRef={bodyEditorRef}
                  onChangeSubject={handleChangeSubject}
                  subjectField={subjectField}
                  attachmentsFields={
                    <AttachmentsFieldList fields={attachmentFieldsAdapter} />
                  }
                  onAttachmentAdded={handleAttachmentAdded}
                  attachmentCount={attachmentFieldsAdapter.length}
                  onTriggerSend={handleTriggerSend}
                  onFileIsUploadingChange={setIsBodyUploading}
                  enduserId={recipientEnduserId}
                />
              </FormField>

              <FormField>
                <Box margin="0 0 300" width="100%">
                  <ToggleBox
                    className={styles.optionalBox}
                    isOpen={showInternalNote}
                    headComponent={ToggleBoxHead}
                    title="Internal note"
                    helpIcon={undefined}
                    input={{
                      id: 'internal-note-toggle-box',
                      name: 'internal-note-toggle-box',
                      value: 'internal-note-toggle-box',
                    }}
                    onChange={(open) => {
                      if (open) {
                        ampli.followUpConversationModalInternalNoteAdded();
                      }
                      setValue('includeInternalNote', open);
                    }}
                  >
                    <ToggleBoxBody>
                      {showInternalNote && (
                        <FormField>
                          <ComposeEmailFieldRichText
                            name="internalNote"
                            label="Internal note"
                            placeholder="Type an internal note"
                            onTriggerSend={handleTriggerSend}
                            onFileIsUploadingChange={setIsNoteUploading}
                            enduserId={recipientEnduserId}
                          />
                        </FormField>
                      )}
                    </ToggleBoxBody>
                  </ToggleBox>
                </Box>
              </FormField>

              <FormField span={6}>
                <ComposeCustomAttributesToggle
                  value={watch('customAttributes')}
                  onChange={(values) => setValue('customAttributes', values)}
                  queueId={watch('queueId')?.[0]}
                  isOpen={watch('customAttributesOpen')}
                  headComponent={ToggleBoxHead}
                />
              </FormField>

              <FormField span={linkType === 'follow_up' ? 3 : 6}>
                <HookFormCategorizedTagsSelect
                  control={control}
                  label="Tags"
                  name="tags"
                  componentProps={{ icon: 'tag' }}
                />
              </FormField>

              {linkType === 'follow_up' && (
                <FormField span={3}>
                  <ComposeEmailFieldCsatLanguage
                    control={control}
                    fromEmailValue={fromEmailValue}
                  />
                </FormField>
              )}

              <ComposeEmailQueueSection control={control} queue={queue} />
            </FormGrid>
          </form>
        </ModalBody>

        <ModalFoot
          cancelName="Cancel"
          cancelOnClick={hide}
          confirmName={sendFailed ? 'Failed – try again' : 'Send'}
          confirmOnClick={() => undefined}
          confirmProps={{
            type: 'submit',
            disabled:
              !isValid ||
              isSubmitting ||
              isAnyUploading ||
              isBuildingTranscript,
            isLoading: isSubmitting || isAnyUploading,
            loadingIndicator: isAnyUploading ? 'Uploading...' : 'Sending...',
            form: formId,
            variant: sendFailed ? 'danger' : 'primary',
          }}
        >
          <Tooltip
            content={
              <CommandKeybindingsTooltipContent commandName="conversation:saveEmailDraft">
                Save draft
              </CommandKeybindingsTooltipContent>
            }
          >
            <Button variant="secondary" onClick={handleSaveDraft}>
              Save draft
            </Button>
          </Tooltip>
        </ModalFoot>
      </>
    );

    return (
      <FormProvider {...form}>
        {showWindowPortal ? (
          <ComposeWindowPortal
            title="Compose email"
            copyStyles
            center="parent"
            name="_modal"
            url=""
            features={{
              width: 900,
              height: 710,
              directories: false,
              titlebar: false,
              toolbar: false,
              location: false,
              status: false,
              menubar: false,
              nodeIntegration: false,
              nativeWindowOpen: true,
            }}
            onUnload={closeWindowPortal}
            onBlock={closeWindowPortal}
            onOpen={(win: Window) => setPopoutWindow(win)}
          >
            <WindowProvider value={popoutWindow ?? window}>
              <div className={styles.popout}>{modalContent}</div>
            </WindowProvider>
          </ComposeWindowPortal>
        ) : (
          <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
            {modalContent}
          </Modal>
        )}
      </FormProvider>
    );
  },
);
